/**
 * Origem Flow: núcleo compartilhado de arrasto (drag and drop).
 *
 * Unifica o ciclo de vida do gesto para todos os motores de arrasto do app
 * (sidebar/v-sortable, kanban, grid e galeria):
 *
 * - Mouse/caneta: o arrasto engaja depois de um pequeno deslocamento
 *   (threshold), para não engolir cliques.
 * - Toque: o arrasto engaja com long-press; se o dedo se mover antes disso,
 *   o gesto é abortado e o scroll nativo vence. Depois de engajado, o
 *   touchmove é bloqueado (non-passive) para a página não rolar junto.
 * - Escape cancela; pointercancel (ex.: o navegador decidiu rolar) idem.
 * - Cursor "grabbing" global e supressão do clique fantasma pós-drop.
 */

const BODY_DRAGGING_CLASS = 'sortable-dragging'

export function startPointerDrag(downEvent, options = {}) {
  const {
    threshold = 3,
    longPressMs = 250,
    touchMoveTolerance = 8,
    onStart = () => {},
    onMove = () => {},
    onUp = () => {},
    onCancel = () => {},
  } = options

  const isTouch = downEvent.pointerType === 'touch'
  const startX = downEvent.clientX
  const startY = downEvent.clientY
  let engaged = false
  let disposed = false
  let lastEvent = downEvent
  let longPressTimeout = null

  const dispose = () => {
    if (disposed) {
      return
    }
    disposed = true
    clearTimeout(longPressTimeout)
    window.removeEventListener('pointermove', pointerMove)
    window.removeEventListener('pointerup', pointerUp)
    window.removeEventListener('pointercancel', pointerCancel)
    window.removeEventListener('touchmove', touchMove)
    window.removeEventListener('contextmenu', contextMenu, true)
    document.body.removeEventListener('keydown', keyDown)
    document.body.classList.remove(BODY_DRAGGING_CLASS)
  }

  const engage = (event) => {
    engaged = true
    document.body.classList.add(BODY_DRAGGING_CLASS)
    onStart(event)
  }

  const pointerMove = (event) => {
    if (disposed) {
      return
    }
    lastEvent = event
    if (!engaged) {
      const distance = Math.max(
        Math.abs(event.clientX - startX),
        Math.abs(event.clientY - startY)
      )
      if (isTouch) {
        // Antes do long-press qualquer movimento significativo indica que o
        // usuário quer rolar a página, não arrastar.
        if (distance > touchMoveTolerance) {
          dispose()
          onCancel(event)
        }
        return
      }
      if (distance > threshold) {
        engage(event)
      } else {
        return
      }
    }
    onMove(event)
  }

  const touchMove = (event) => {
    // Bloqueia o scroll nativo apenas depois do arrasto engajar.
    if (engaged) {
      event.preventDefault()
    }
  }

  const pointerUp = (event) => {
    const wasEngaged = engaged
    dispose()
    onUp(event, wasEngaged)
  }

  const pointerCancel = (event) => {
    dispose()
    onCancel(event)
  }

  const keyDown = (event) => {
    if (event.key === 'Escape') {
      dispose()
      onCancel(event)
    }
  }

  const contextMenu = (event) => {
    // Long-press no toque dispara menu de contexto em alguns navegadores.
    if (engaged) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  window.addEventListener('pointermove', pointerMove)
  window.addEventListener('pointerup', pointerUp)
  window.addEventListener('pointercancel', pointerCancel)
  window.addEventListener('touchmove', touchMove, { passive: false })
  window.addEventListener('contextmenu', contextMenu, true)
  document.body.addEventListener('keydown', keyDown)

  if (isTouch) {
    longPressTimeout = setTimeout(() => {
      if (!disposed && !engaged) {
        engage(lastEvent)
      }
    }, longPressMs)
  }

  return { dispose, isEngaged: () => engaged }
}

/**
 * Depois de um drop, o navegador ainda dispara um click no elemento sob o
 * cursor; isto o suprime para o drop não virar um clique acidental.
 */
export function suppressNextClick() {
  const prevent = (event) => {
    event.stopPropagation()
    window.removeEventListener('click', prevent, true)
  }
  window.addEventListener('click', prevent, true)
  setTimeout(() => {
    window.removeEventListener('click', prevent, true)
  })
}

/**
 * Animação de "pouso": desliza um elemento fantasma (position fixed/absolute
 * no body) até o retângulo alvo e resolve quando terminar. O chamador remove
 * o elemento depois.
 */
export function animateGhostTo(ghost, targetRect, { duration = 150 } = {}) {
  return new Promise((resolve) => {
    if (!ghost || !targetRect) {
      resolve()
      return
    }
    ghost.style.transition = `left ${duration}ms ease-out, top ${duration}ms ease-out, transform ${duration}ms ease-out, opacity ${duration}ms ease-out`
    ghost.style.left = targetRect.left + 'px'
    ghost.style.top = targetRect.top + 'px'
    ghost.style.transform = 'none'
    setTimeout(resolve, duration + 20)
  })
}
